import { useCallback, useEffect, useRef, useState } from "react";
import { downsample, encodeWav, joinChunks, levelOf, pauseDetector } from "../../../lib/artemis/wav";
import { READ_MODES, rankVoices } from "../../../lib/artemis/voice";
import { createSpeechQueue } from "../../../lib/artemis/speech-queue";

// Speech recognition and read aloud for Artemis (WEB-7116). Dictation fills the
// question box; nothing is sent until the user sends it.

const Recognition = () => (typeof window !== 'undefined' && (window.SpeechRecognition || window.webkitSpeechRecognition)) || null
const READ_ALOUD_KEY = 'artemis.readAloud'

// { supported, listening, error, start(baseText), stop() }. onText gets the box's whole text:
// what was there before, then what's been heard so far.
export const useDictation = onText => {
  const [listening, setListening] = useState(false)
  // null, 'denied' (the user or browser blocked the mic) or 'failed'.
  const [error, setError] = useState(null)
  const recognition = useRef(null)
  const supported = !!Recognition()

  const stop = useCallback(() => {
    if (recognition.current) recognition.current.stop()
  }, [])

  const start = useCallback(baseText => {
    const Speech = Recognition()
    if (!Speech) return
    if (recognition.current) recognition.current.abort()
    const r = new Speech()
    r.lang = (typeof navigator !== 'undefined' && navigator.language) || 'en-US'
    r.interimResults = true
    r.continuous = false
    const before = baseText && baseText.trim() ? `${baseText.trim()} ` : ''
    r.onresult = event => {
      let heard = ''
      for (let i = 0; i < event.results.length; i++) heard += event.results[i][0].transcript
      onText(`${before}${heard.trim()}`)
    }
    r.onerror = event => setError(event.error === 'not-allowed' || event.error === 'service-not-allowed' ? 'denied' : event.error === 'no-speech' || event.error === 'aborted' ? null : 'failed')
    r.onend = () => { setListening(false); recognition.current = null }
    recognition.current = r
    setError(null)
    setListening(true)
    try { r.start() } catch (e) { setListening(false); setError('failed') }
  }, [onText])

  useEffect(() => () => { if (recognition.current) recognition.current.abort() }, [])

  return { supported, listening, error, start, stop }
}

// Records the question and sends it to Artemis's Transcribe (API-3691), which knows hunting and
// map terms ("layer", not "slayer") and the units in view. The same shape as useDictation, plus
// busy while the audio is being transcribed.
//   transcribe(wavBlob) resolves with { Text }; a 404 rejection means this server has none.
const CAPTURE_WORKLET = `class ArtemisCapture extends AudioWorkletProcessor {
  process(inputs) { const channel = inputs[0] && inputs[0][0]; if (channel) this.port.postMessage(channel.slice(0)); return true }
}
registerProcessor('artemis-capture', ArtemisCapture)`
const MIN_MS = 400

export const canRecord = () => typeof navigator !== 'undefined' && !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia) &&
  typeof window !== 'undefined' && !!(window.AudioContext || window.webkitAudioContext)

export const useServerDictation = (onText, transcribe, onUnavailable) => {
  const [listening, setListening] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const session = useRef(null)
  const supported = canRecord()

  const finish = useCallback(async (send = true) => {
    const s = session.current
    if (!s) return
    session.current = null
    setListening(false)
    s.stream.getTracks().forEach(t => t.stop())
    try { s.node.disconnect() } catch (e) { /* already gone */ }
    const rate = s.ctx.sampleRate
    s.ctx.close().catch(() => {})
    const samples = joinChunks(s.chunks)
    if (!send || samples.length < (rate * MIN_MS) / 1000) return
    setBusy(true)
    try {
      const wav = new Blob([encodeWav(downsample(samples, rate))], { type: 'audio/wav' })
      const result = await transcribe(wav)
      // Staging answers { text, raw, … } though the contract says { Text, Raw, … }: read either.
      const said = result && (typeof result.Text === 'string' ? result.Text : result.text)
      const text = typeof said === 'string' ? said.trim() : ''
      if (text) onText(`${s.before}${text}`)
      else setError('failed')
    } catch (err) {
      if (err && err.code === 'http_404') onUnavailable()
      setError(err && err.code === 'http_429' ? 'busy' : 'failed')
    } finally {
      setBusy(false)
    }
  }, [onText, transcribe, onUnavailable])

  const stop = useCallback(() => { finish(true) }, [finish])

  const start = useCallback(async baseText => {
    if (!supported || session.current || busy) return
    setError(null)
    let stream
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true, autoGainControl: true } })
    } catch (e) {
      setError(e && (e.name === 'NotAllowedError' || e.name === 'SecurityError') ? 'denied' : 'failed')
      return
    }
    // The context runs at the device's rate (asking for 16 kHz fails with some streams); the
    // samples are downsampled before encoding.
    const Context = window.AudioContext || window.webkitAudioContext
    const ctx = new Context()
    const source = ctx.createMediaStreamSource(stream)
    const quiet = ctx.createGain()
    quiet.gain.value = 0
    const s = { stream, ctx, chunks: [], before: baseText && baseText.trim() ? `${baseText.trim()} ` : '', ended: pauseDetector() }
    const onChunk = chunk => {
      if (session.current !== s) return
      s.chunks.push(chunk)
      if (s.ended(levelOf(chunk), (chunk.length / ctx.sampleRate) * 1000)) finish(true)
    }
    try {
      if (ctx.audioWorklet) {
        const url = URL.createObjectURL(new Blob([CAPTURE_WORKLET], { type: 'application/javascript' }))
        await ctx.audioWorklet.addModule(url)
        URL.revokeObjectURL(url)
        s.node = new window.AudioWorkletNode(ctx, 'artemis-capture')
        s.node.port.onmessage = event => onChunk(event.data)
      } else {
        // Older Safari: no AudioWorklet.
        s.node = ctx.createScriptProcessor(4096, 1, 1)
        s.node.onaudioprocess = event => onChunk(event.inputBuffer.getChannelData(0).slice(0))
      }
      source.connect(s.node)
      s.node.connect(quiet)
      quiet.connect(ctx.destination)
    } catch (e) {
      stream.getTracks().forEach(t => t.stop())
      ctx.close().catch(() => {})
      setError('failed')
      return
    }
    session.current = s
    setListening(true)
  }, [supported, busy, finish])

  useEffect(() => () => { finish(false) }, [])

  return { supported, listening, busy, error, start, stop }
}

// Reading replies aloud (WEB-7116, WEB-7123). Artemis's own voice (Amazon Polly, API-3703) where
// the server has it, one clip per sentence; this browser's best voice where it doesn't, or when a
// clip can't be fetched or played. What's said and when is the queue's (speech-queue.js).
//   api.speak({ text, voice }, signal) resolves with an audio Blob; api.voices() with
//   { Items: [{ Id, Name, Gender, Engine }], Default }.
// Returns { supported, enabled, setEnabled, mode, setMode, voice, setVoice, voices, server,
// loadVoices, speaking, progress(text), say(text), preview(text), stop(), unlock() }.
// Read aloud, the mode and the voice are remembered in this browser.
const MODE_KEY = 'artemis.readAloudMode'
const VOICE_KEY = 'artemis.readAloudVoice'
// Whether this server can speak; no Speech (404), no Polly (502) or no answer turns it off for the page.
const POLLY = { available: true }
const SERVER_OFF_CODES = new Set(['http_404', 'http_501', 'http_502', 'http_503'])
// A moment of silence, played while the user is clicking Send so Safari lets later clips play.
const SILENT_WAV = 'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAIA+AAACABAAZGF0YQAAAAA='

const stored = (key, fallback) => {
  try { return window.localStorage.getItem(key) || fallback } catch (e) { return fallback }
}
const store = (key, value) => {
  try { window.localStorage.setItem(key, value) } catch (e) { /* private window: just not remembered */ }
}
const pageLanguage = () => (typeof navigator !== 'undefined' && navigator.language) || 'en-US'
const silentClip = { ready: Promise.resolve(), play: () => Promise.resolve(), cancel() {} }

export const useSpeaker = api => {
  const synth = typeof window !== 'undefined' && window.speechSynthesis ? window.speechSynthesis : null
  const canPlayAudio = typeof window !== 'undefined' && typeof window.Audio === 'function'
  const [enabled, setEnabledState] = useState(() => (!!synth || canPlayAudio) && stored(READ_ALOUD_KEY, 'off') === 'on')
  // A short summary unless the user has picked the whole answer.
  const [mode, setModeState] = useState(() => (READ_MODES.includes(stored(MODE_KEY, 'summary')) ? stored(MODE_KEY, 'summary') : 'summary'))
  // 'auto' (Artemis's voice, else this browser's best), 'polly:<Id>' or 'browser:<voiceURI>'.
  const [voice, setVoiceState] = useState(() => stored(VOICE_KEY, 'auto'))
  const [browserVoices, setBrowserVoices] = useState([])
  const [serverVoices, setServerVoices] = useState(null)
  const [server, setServer] = useState(POLLY.available && canPlayAudio)
  const [speaking, setSpeaking] = useState(false)
  const audio = useRef(null)
  const settings = useRef({})
  settings.current = { voice, browserVoices, server }

  // Browsers load their voices late; Chrome says so with voiceschanged.
  useEffect(() => {
    if (!synth) return
    const load = () => setBrowserVoices(rankVoices(synth.getVoices(), pageLanguage()))
    load()
    synth.addEventListener && synth.addEventListener('voiceschanged', load)
    return () => synth.removeEventListener && synth.removeEventListener('voiceschanged', load)
  }, [synth])

  const serverOff = useCallback(() => { POLLY.available = false; setServer(false) }, [])
  const audioElement = () => {
    if (!audio.current && canPlayAudio) audio.current = new window.Audio()
    return audio.current
  }

  const browserVoice = () => {
    const { voice: chosen, browserVoices: ranked } = settings.current
    const picked = chosen.startsWith('browser:') && ranked.find(v => v.voiceURI === chosen.slice(8))
    return picked || ranked[0] || null
  }

  const browserClip = text => {
    if (!synth) return silentClip
    let utterance = null
    return {
      ready: Promise.resolve(),
      play: () => new Promise(resolve => {
        utterance = new window.SpeechSynthesisUtterance(text)
        const v = browserVoice()
        if (v) { utterance.voice = v; utterance.lang = v.lang } else utterance.lang = pageLanguage()
        utterance.onend = () => resolve()
        utterance.onerror = () => resolve()
        synth.speak(utterance)
      }),
      cancel: () => { if (utterance) synth.cancel() }
    }
  }

  const serverClip = (text, voiceId) => {
    const controller = new AbortController()
    let url = null, fallback = null, cancelled = false
    const ready = api.speak({ text, voice: voiceId }, controller.signal).then(
      blob => { url = URL.createObjectURL(blob) },
      err => {
        if (cancelled) throw err
        // A rejected text or a busy minute falls back for this clip; no Polly, for the page.
        if (!err || !err.code || SERVER_OFF_CODES.has(err.code)) serverOff()
        fallback = browserClip(text)
      })
    const release = () => { if (url) { URL.revokeObjectURL(url); url = null } }
    return {
      ready,
      play: () => {
        if (fallback) return fallback.play()
        const el = audioElement()
        return new Promise(resolve => {
          const done = () => { el.onended = null; el.onerror = null; release(); resolve() }
          el.onended = done
          el.onerror = done
          el.src = url
          // Blocked (no click yet, in Safari): this browser's voice says it instead.
          el.play().catch(() => {
            el.onended = null
            el.onerror = null
            release()
            if (cancelled) return resolve()
            fallback = browserClip(text)
            fallback.play().then(resolve)
          })
        })
      },
      cancel: () => {
        cancelled = true
        controller.abort()
        if (audio.current && url && audio.current.src === url) audio.current.pause()
        release()
        if (fallback) fallback.cancel()
      }
    }
  }

  const prepare = text => {
    const { voice: chosen, server: onServer } = settings.current
    if (onServer && !chosen.startsWith('browser:')) return serverClip(text, chosen.startsWith('polly:') ? chosen.slice(6) : null)
    return browserClip(text)
  }
  const prepareRef = useRef(prepare)
  prepareRef.current = prepare
  const queue = useRef(null)
  if (!queue.current) queue.current = createSpeechQueue({ prepare: (text, kind) => prepareRef.current(text, kind), onSpeaking: setSpeaking })

  const stop = useCallback(() => {
    queue.current.stop()
    if (synth) synth.cancel()
    if (audio.current) audio.current.pause()
  }, [synth])

  const progress = useCallback(text => queue.current.progress(text), [])
  const say = useCallback(text => queue.current.answer(text), [])
  const preview = useCallback(text => { stop(); queue.current.answer(text) }, [stop])

  // Called from a click or key press (Send), so audio played later in this page isn't blocked.
  const unlock = useCallback(() => {
    if (!settings.current.server) return
    const el = audioElement()
    if (!el || el.dataset.unlocked) return
    el.dataset.unlocked = 'yes'
    el.src = SILENT_WAV
    el.play().catch(() => { delete el.dataset.unlocked })
  }, [])

  // Artemis's voices, for the picker: fetched once, when the menu opens.
  const loadVoices = useCallback(() => {
    if (!settings.current.server || serverVoices) return
    api.voices().then(setServerVoices, err => { if (!err || !err.code || SERVER_OFF_CODES.has(err.code)) serverOff() })
  }, [serverVoices])

  const setEnabled = useCallback(on => {
    setEnabledState(on)
    store(READ_ALOUD_KEY, on ? 'on' : 'off')
    if (!on) stop()
  }, [stop])
  const setMode = useCallback(next => { setModeState(next); store(MODE_KEY, next) }, [])
  const setVoice = useCallback(next => { setVoiceState(next); store(VOICE_KEY, next) }, [])

  useEffect(() => () => stop(), [stop])

  return {
    supported: !!synth || canPlayAudio,
    enabled, setEnabled, mode, setMode, voice, setVoice, server, loadVoices,
    voices: {
      server: server && serverVoices && Array.isArray(serverVoices.Items) ? serverVoices.Items : [],
      serverDefault: serverVoices && serverVoices.Default,
      browser: browserVoices
    },
    speaking, progress, say, preview, stop, unlock
  }
}
